import { axiosInstance } from "@/lib/axios";
import type {
  BidItem,
  BidsPageMeta,
  BidsQueryParams,
  BidsResponse,
  SetWinnerPayload,
  SingleBidResponse,
  StoreBidPayload,
  UpdateBidStatusPayload,
} from "./types";

const EMPTY_PAGE: BidsPageMeta = {
  current_page: 1,
  last_page: 1,
  per_page: 0,
  total: 0,
};

function normalizeBidItem(raw: any): BidItem {
  return {
    id: Number(raw?.id ?? 0),
    price: Number(raw?.price ?? 0),
    status: raw?.status ?? "pending",
    is_win: Boolean(raw?.is_win),
    user_id: raw?.user_id != null ? Number(raw.user_id) : null,
    auction_id: raw?.auction_id != null ? Number(raw.auction_id) : null,
    network_id: raw?.network_id != null ? Number(raw.network_id) : null,
    currency_id: raw?.currency_id != null ? Number(raw.currency_id) : null,
    created_at: raw?.created_at,
    updated_at: raw?.updated_at,
    user: raw?.user ?? null,
    currency: raw?.currency ?? null,
    network: raw?.network ?? null,
    auction: raw?.auction ?? null,
  };
}

/**
 * The bids an auction's own response carries (`/auctions/{slug}` → `auction.bids`).
 * `/bids?auction_id=` answers 404 once an auction has finished, so for a closed
 * auction these are the only bids there are.
 */
export function readEmbeddedBids(raw: unknown): BidItem[] {
  return Array.isArray(raw) ? raw.filter((b) => b && typeof b === "object" && (b as { id?: unknown }).id != null).map(normalizeBidItem) : [];
}

/** The board and the auction's own bids as one list, highest first; a bid on both is the board's (it has the dates). */
export function mergeBids(board: BidItem[], embedded: BidItem[]): BidItem[] {
  const seen = new Set(board.map((b) => b.id));
  return [...board, ...embedded.filter((b) => !seen.has(b.id))].sort((a, b) => Number(b.price) - Number(a.price));
}

/**
 * The `/bids` envelope, normalised. THE LIST IS UNDER `data.bid` — singular
 * (verified live: `{ "data": { "bid": [], "page": {…} } }`). This read only
 * `items` / `bids`, fell through to the `data` OBJECT, which is not an
 * array — so every board came back empty, the outcome of a new bid was
 * judged against no bids at all, and the bidder's own previous bids were
 * never seen by validation. Shared by `getBids` and the auction page's
 * server-side first read, so the two can never disagree again.
 */
export function toBidsResponse(raw: any): BidsResponse {
  // Through the browser's proxy the list arrives bare — `{ "bid": [], "page": {…} }`, no
  // `data` (verified live 2026-10-03) — so both envelopes are read, the bare one too.
  const d = raw?.data && typeof raw.data === "object" ? raw.data : raw;
  const list = d?.items ?? d?.bids ?? d?.bid ?? (Array.isArray(d) ? d : []);
  return {
    data: {
      items: Array.isArray(list) ? list.map(normalizeBidItem) : [],
      page: normalizePageMeta(d?.page ?? raw?.meta ?? d),
    },
    message: raw?.message ?? null,
    next: raw?.next ?? null,
    errors: raw?.errors ?? null,
    meta: raw?.meta ?? [],
  };
}

function normalizePageMeta(raw: any): BidsPageMeta {
  const page = raw?.page ?? raw?.pagination ?? raw ?? {};
  return {
    current_page: Number(page?.current_page ?? 1),
    last_page: Number(page?.last_page ?? 1),
    per_page: Number(page?.per_page ?? 0),
    total: Number(page?.total ?? 0),
  };
}

export async function getBids(params: BidsQueryParams = {}): Promise<BidsResponse> {
  const search = new URLSearchParams();
  if (params.user_id) search.set("user_id", String(params.user_id));
  if (params.auction_id) search.set("auction_id", String(params.auction_id));
  // The list filter takes an integer (`1` / `0`); `true` / `false` answer 422.
  if (typeof params.is_win === "boolean") search.set("is_win", params.is_win ? "1" : "0");
  if (params.currency_id) search.set("currency_id", String(params.currency_id));
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);

  const { data } = await axiosInstance.get(`/bids${search.toString() ? `?${search.toString()}` : ""}`, {
    headers: params.lang ? { "lang": params.lang } : undefined,
  });

  return toBidsResponse(data);
}

export async function storeBid(auctionId: number | string, payload: StoreBidPayload): Promise<SingleBidResponse> {
  const { data } = await axiosInstance.post(`/bids/store-bid/${auctionId}`, payload);
  const rawItem = data?.data?.item ?? data?.data?.bid ?? data?.data;
  return {
    data: { item: normalizeBidItem(rawItem) },
    message: data?.message ?? null,
    next: data?.next ?? null,
    errors: data?.errors ?? null,
    meta: data?.meta ?? [],
  };
}

export async function updateBidStatus(bidId: number | string, payload: UpdateBidStatusPayload): Promise<SingleBidResponse> {
  const { data } = await axiosInstance.post(`/bids/status/${bidId}`, payload);
  const rawItem = data?.data?.item ?? data?.data?.bid ?? data?.data;
  return {
    data: { item: normalizeBidItem(rawItem) },
    message: data?.message ?? null,
    next: data?.next ?? null,
    errors: data?.errors ?? null,
    meta: data?.meta ?? [],
  };
}

export async function setBidWinner(bidId: number | string, payload: SetWinnerPayload): Promise<SingleBidResponse> {
  const { data } = await axiosInstance.post(`/bids/set-winner/${bidId}`, payload);
  const rawItem = data?.data?.item ?? data?.data?.bid ?? data?.data;
  return {
    data: { item: normalizeBidItem(rawItem) },
    message: data?.message ?? null,
    next: data?.next ?? null,
    errors: data?.errors ?? null,
    meta: data?.meta ?? [],
  };
}

export function getEmptyBidsResponse(): BidsResponse {
  return {
    data: {
      items: [],
      page: EMPTY_PAGE,
    },
    message: null,
    next: null,
    errors: null,
    meta: [],
  };
}
